import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { translate } from "../../../../config/localisation";
import configs from "../../../../config/config";

// Роли, доступные для прямого добавления в организацию
const ROLES = [
  { value: 1, key: "Annotator" },
  { value: 2, key: "Reviewer" },
  { value: 3, key: "Super Checker" },
  { value: 4, key: "Workspace Manager" },
];

const empty = {
  email: "",
  password: "",
  username: "",
  first_name: "",
  last_name: "",
  role: 1,
  workspace_id: "",
};

// Прямое создание пользователя (email + пароль), без приглашения по почте.
const AddUserDialog = ({ open, onClose, orgId, onSuccess }) => {
  const [form, setForm] = useState(empty);
  const [workspaces, setWorkspaces] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Роль создаваемого — строго ниже своей (совпадает с проверкой на бэкенде),
  // чтобы менеджер не выбирал недоступные роли и не ловил 403.
  const loggedInUser = useSelector((state) => state.fetchLoggedInUserData?.data);
  const authority =
    loggedInUser?.is_superuser || loggedInUser?.role === 6
      ? 6
      : loggedInUser?.role || 0;
  const availableRoles = ROLES.filter((r) => r.value < authority);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // загрузить список рабочих пространств организации при открытии
  useEffect(() => {
    if (!open) return;
    fetch(configs.BASE_URL_AUTO + "/workspaces/", {
      headers: { Authorization: "JWT " + localStorage.getItem("csi_access_token") },
    })
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : d.results || [];
        setWorkspaces(
          list.filter((w) => !orgId || Number(w.organization) === Number(orgId))
        );
      })
      .catch(() => setWorkspaces([]));
  }, [open, orgId]);

  const close = () => {
    setForm(empty);
    setMsg("");
    onClose();
  };

  const submit = async () => {
    setLoading(true);
    setMsg("");
    try {
      const r = await fetch(configs.BASE_URL_AUTO + "/users/invite/add_user/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "JWT " + localStorage.getItem("csi_access_token"),
        },
        body: JSON.stringify({ ...form, organization_id: orgId }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setForm(empty);
        onSuccess && onSuccess(d.message);
        onClose();
      } else {
        setMsg(d.message || "Ошибка при создании пользователя");
      }
    } catch (e) {
      setMsg(String(e));
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{translate("Add User")}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth margin="dense" label="Email"
          value={form.email} onChange={set("email")}
        />
        <TextField
          fullWidth margin="dense" label={translate("Password")} type="text"
          value={form.password} onChange={set("password")}
        />
        <TextField
          fullWidth margin="dense" label={translate("Nickname")}
          value={form.username} onChange={set("username")}
          helperText={translate("Nickname hint")}
        />
        <TextField
          fullWidth margin="dense" label={translate("First Name")}
          value={form.first_name} onChange={set("first_name")}
        />
        <TextField
          fullWidth margin="dense" label={translate("Last Name")}
          value={form.last_name} onChange={set("last_name")}
        />
        <TextField
          fullWidth margin="dense" select label={translate("Role")}
          value={form.role} onChange={set("role")}
        >
          {availableRoles.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {translate(r.key)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth margin="dense" select label={translate("Workspace")}
          value={form.workspace_id} onChange={set("workspace_id")}
        >
          <MenuItem value="">— {translate("None")} —</MenuItem>
          {workspaces.map((w) => (
            <MenuItem key={w.id} value={w.id}>
              {w.workspace_name}
            </MenuItem>
          ))}
        </TextField>
        {msg && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {msg}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{translate("Cancel")}</Button>
        <Button variant="contained" disabled={loading} onClick={submit}>
          {translate("Add User")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;
